/**
 * @packageDocumentation
 * @module API
 * @category Core
 */
import { HttpEventType, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { concatMap, map, of, take, tap } from 'rxjs';

import { SKIP_TOKEN_INTERCEPTOR_HEADER } from '../auth/consts/skip-token-interceptor-header.const';
import { AuthTokenState } from '../auth/states/auth-token.state';
import { AuthTokenStateActions } from '../auth/states/auth-token.state.actions';
import { SnackBarService } from '../snack-bar';

const SERVER_STATUS = 200;

function addToken(req: HttpRequest<unknown>, token: string | null): HttpRequest<unknown> {
  const alreadyHasToken = !!req.headers.get('authorization');

  return !token || alreadyHasToken ? req : req.clone({ setHeaders: { Authorization: `Bearer ${token}` } });
}

/**
 * Добавление токена авторизации в HTTP-запросы.
 * При необходимости выполняет рефреш токена с последующей переотправкой запроса.
 *
 * @group Interceptors
 *
 * @param originalReq
 * @param next
 * @returns
 */
export const authTokenInterceptor: HttpInterceptorFn = (originalReq, next) => {
  const store = inject(Store);
  const snackBar = inject(SnackBarService);
  const translate = inject(TranslateService);

  if (originalReq.headers.has(SKIP_TOKEN_INTERCEPTOR_HEADER)) {
    const headers = originalReq.headers.delete(SKIP_TOKEN_INTERCEPTOR_HEADER);

    return next(originalReq.clone({ headers }));
  }

  return store.select(AuthTokenState.accessToken).pipe(
    take(1),
    map((accessToken) => addToken(originalReq, accessToken)),
    concatMap((request) => next(request)),
    concatMap((event) => {
      let needToAuthenticate = false;
      let forbidden = false;

      if (
        event.type === HttpEventType.Response &&
        event.status === SERVER_STATUS &&
        event.body &&
        typeof event.body === 'object' &&
        'errors' in event.body &&
        Array.isArray(event.body.errors)
      ) {
        needToAuthenticate = !!event.body.errors.find((e) => e.extensions && e.extensions.code === 'UNAUTHENTICATED');

        forbidden = !!event.body.errors.find((e) => e.extensions && e.extensions.code === 'FORBIDDEN');
      }

      if (needToAuthenticate) {
        return store.dispatch(new AuthTokenStateActions.Refresh()).pipe(
          concatMap(() => store.select(AuthTokenState.accessToken).pipe(take(1))),
          tap((token) => {
            if (!token) {
              store.dispatch(new AuthTokenStateActions.Logout());
            }
          }),
          map((accessToken) => addToken(originalReq, accessToken)),
          concatMap((req) => next(req)),
        );
      }

      if (forbidden) {
        console.warn('forbidden', event);
        snackBar.showWarningMessage(translate.instant('CORE.ACCESS_DENIED')); // Нет доступа
      }

      return of(event);
    }),
  );
};
